import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Stack, Typography } from '@mui/material';

import { CheckboxCheckedIcon, CheckboxUncheckedIcon } from '@/assets/icons';
import { NumUtils } from '@/modules/common/utils';

import { deSelectedSx, selectedSx } from '../../../styles/PropertySelectCard.styles';
import { SpecificationItem } from '../../common/SpecificationItem';
import { LoadCarrierTypeV1 } from '../types';

type Props = {
  item: LoadCarrierTypeV1;
  selected: boolean;
  onClick: (id: string) => void;
};

export const LoadCarrierTypeCard = ({
  item: { id, name, length, width, height, weight, safeWorkingLoad, image },
  selected,
  onClick,
}: Props) => {
  const { t } = useTranslation(['interface']);
  const [isHover, setIsHover] = useState(false);

  const handleMouseEnter = useCallback(() => {
    setIsHover(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHover(false);
  }, []);

  const onClickInner = useCallback(() => {
    onClick(id);
  }, [onClick, id]);

  const icon = useMemo(() => {
    if (selected) {
      return <CheckboxCheckedIcon className='check' />;
    }
    if (isHover) {
      return <CheckboxUncheckedIcon className='check' />;
    }

    return null;
  }, [isHover, selected]);

  return (
    <Stack>
      <Button
        onClick={onClickInner}
        sx={selected ? selectedSx : deSelectedSx}
        aria-selected={selected}
        aria-label='load carrier option button'
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Stack width='100%' height='100%'>
          <img
            src={image.url}
            alt={name}
            style={{
              maxWidth: 'calc(197px - 1.6px)',
              maxHeight: 'calc(195px - 1.6px)',
              padding: 8,
              marginTop: 'auto',
              marginBottom: 'auto',
            }}
          />
          <Stack
            className='label-box'
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography fontWeight='bold' align='left' fontSize={14}>
              {name}
            </Typography>
            {icon}
          </Stack>
        </Stack>
      </Button>
      <Stack gap={1} mx={1} mt={2}>
        <Typography fontSize={13} color='neutral.main' textTransform='uppercase'>
          {t('setup.floorPlan.load_carrier_types_select_dialog.specifications', 'Specifications')}
        </Typography>
        <SpecificationItem
          i18nKey='interface:setup.floorPlan.property.specifications.length'
          value={NumUtils.metersToMmRounded(length)}
          unit='mm'
        />
        <SpecificationItem
          i18nKey='interface:setup.floorPlan.property.specifications.width'
          value={NumUtils.metersToMmRounded(width)}
          unit='mm'
        />
        <SpecificationItem
          i18nKey='interface:setup.floorPlan.property.specifications.height'
          value={NumUtils.metersToMmRounded(height)}
          unit='mm'
        />
        <SpecificationItem
          i18nKey='interface:setup.floorPlan.property.specifications.weight'
          value={weight}
          unit='kg'
        />
        <SpecificationItem
          i18nKey='interface:setup.floorPlan.property.specifications.safeWorkingLoad'
          value={safeWorkingLoad}
          unit='kg'
        />
      </Stack>
    </Stack>
  );
};
